import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import { getErrorStyle } from 'App/Map/UserSettingsMenu/Shared/Alerts/_utils/getErrorStyle'
import { ErrorText } from 'components/ErrorText/ErrorText'
import { translate } from 'i18n/i18n'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useState } from 'react'
import type { TIrrigationZoneEmitterId } from 'stores/fieldAssetStore/helperTypes'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { EmitterConfigurationNumberInput } from '../../../components/EmitterConfigurationNumberInput'
import type { ModifiedZone } from '../../../EmitterConfiguration'
import { useEmitterConfigurationUnit } from '../../../hooks/useEmitterConfigurationUnit'

type TEmitterConfigurationZoneDetail = {
  id: TIrrigationZoneEmitterId
  flowRate: number
  unitPerHour: string
  emitterType: TEmitterType
  zoneId: number
  modifiedZones: ModifiedZone[]
  setModifiedZones: Dispatch<SetStateAction<ModifiedZone[]>>
  propertyId: number
}

export const EmitterConfigurationZoneDetail = ({
  id,
  flowRate,
  unitPerHour,
  emitterType,
  zoneId,
  modifiedZones,
  setModifiedZones,
  propertyId,
}: TEmitterConfigurationZoneDetail) => {
  const isWideScreen = useIsWideScreen()
  const { unit, dbUnitPerHour, precision } = useEmitterConfigurationUnit()

  const convertedFlowRateValue =
    unitConverter
      .irrigationApplicationRate(flowRate, {
        inputUnit: unitPerHour === 'INCH' ? 'in' : 'mm',
        outputUnit: dbUnitPerHour === 'INCH' ? 'in' : 'mm',
        decimalPlaces: precision,
      })
      .value() ?? flowRate

  const [numberValue, setNumberValue] = useState<number | ''>(convertedFlowRateValue)

  // Update the default value when the flow rate changes from bulk edit
  useEffect(() => {
    setNumberValue(convertedFlowRateValue)
  }, [convertedFlowRateValue])

  useEffect(() => {
    if (!modifiedZones.find((zone) => zone.id === id)) {
      setNumberValue(convertedFlowRateValue)
    }
  }, [modifiedZones])

  const errorStyle = getErrorStyle()
  const isValid = Number(numberValue) > 0

  return (
    <>
      <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div css={{ display: 'flex', alignItems: 'center' }}>
          <div>{mapApiEmitterTypeToLabel(emitterType)}</div>
        </div>
        <div>
          <EmitterConfigurationNumberInput
            unit={unit}
            value={numberValue}
            css={{ width: isWideScreen ? 160 : 110 }}
            onChange={(value) => {
              setNumberValue(value)

              if (value !== convertedFlowRateValue) {
                const newModifiedZone = {
                  emitterType,
                  id,
                  zoneId,
                  propertyId,
                  flowRate: Number(value),
                }

                setModifiedZones((prev) => {
                  const otherZones = prev.filter((zone) => zone.id !== id)

                  return [...otherZones, newModifiedZone]
                })
              } else {
                setModifiedZones((prev) => prev.filter((zone) => zone.id !== id))
              }
            }}
            classNames={{ input: !isValid ? errorStyle : undefined }}
          />
        </div>
      </div>
      {!isValid && (
        <div css={{ display: 'flex', justifyContent: 'flex-end', marginTop: 5 }}>
          <ErrorText>{translate.phrases.banyanApp('Value must be greater than 0')}</ErrorText>
        </div>
      )}
    </>
  )
}
